/**
 * O Leilão no modo mock (HUD V3 §11): umas ofertas de outros jogadores em memória, para a tela ter o que
 * mostrar e o teste de tela exercitar comprar, dar lance, anunciar e cancelar. Fictício: nada aqui sai do
 * navegador. As regras de preço (lance mínimo, próximo lance, preço fixo) seguem as do servidor.
 */
import type {
  MercadoCatalogo, MercadoCatalogoLinha, MercadoItem, MercadoMeus, MercadoOferta, MercadoOfertaLinha
} from './types';
import { ApiError } from './types';
import { MOCK_ITEM_DEFS } from './itemCatalog';
import { BASES_COM_MATERIAL, baseDe } from '../look/materiais';

type Lance = { nome: string; teto: number; seu: boolean; em: string };
type Lote = {
  id: string; defId: string; vendedor: string; seu: boolean; qty: number; dur: number | null; durMax: number | null;
  material: string | null; lanceMinimo: number; compraJa: number | null; terminaEm: string; lances: Lance[];
};

const POCOES = new Set(['greater-healing', 'cure-poison', 'healing', 'mana']);
const LOJA: Readonly<Record<string, number>> = { katana: 6500, dagger: 2100, bandage: 5, heater: 4200, bow: 3900 };

export function grupoMock(defId: string, slot: string): string {
  if (defId === 'arrow' || defId === 'bolt') return 'ammo';
  if (defId.endsWith('-ore')) return 'ore';
  if (defId.endsWith('-ingot')) return 'ingot';
  if (POCOES.has(defId)) return 'potion';
  if (slot === 'weapon') return 'weapon';
  if (slot === 'shield') return 'shield';
  if (slot === 'cloth') return 'cloth';
  if (slot === 'mount') return 'mount';
  if (['chest', 'arms', 'gloves', 'legs', 'helm'].includes(slot)) return 'armor';
  return 'misc';
}

const em = (h: number) => new Date(Date.now() + h * 3600_000).toISOString();
let seq = 0;
const novoId = () => `mock-lote-${++seq}`;

const lotes: Lote[] = [
  { id: novoId(), defId: 'katana', vendedor: 'Lyra', seu: false, qty: 1, dur: 52, durMax: 60, material: null, lanceMinimo: 4800, compraJa: 4800, terminaEm: em(20), lances: [] },
  { id: novoId(), defId: 'katana', vendedor: 'Thorne', seu: false, qty: 1, dur: 60, durMax: 60, material: 'valorite', lanceMinimo: 9000, compraJa: 14000, terminaEm: em(0.4), lances: [{ nome: 'Maeve', teto: 9450, seu: false, em: em(-1) }] },
  { id: novoId(), defId: 'katana', vendedor: 'Kael', seu: false, qty: 1, dur: 40, durMax: 60, material: null, lanceMinimo: 3200, compraJa: null, terminaEm: em(30), lances: [] },
  { id: novoId(), defId: 'bone', vendedor: 'Bram', seu: false, qty: 50, dur: null, durMax: null, material: null, lanceMinimo: 400, compraJa: 400, terminaEm: em(11), lances: [] },
  { id: novoId(), defId: 'nightshade', vendedor: 'Selene', seu: false, qty: 20, dur: null, durMax: null, material: null, lanceMinimo: 100, compraJa: 150, terminaEm: em(6), lances: [] },
  { id: novoId(), defId: 'bone-chest', vendedor: 'Corvin', seu: false, qty: 1, dur: 45, durMax: 50, material: null, lanceMinimo: 2500, compraJa: null, terminaEm: em(1.5), lances: [{ nome: 'Ysolde', teto: 2700, seu: false, em: em(-2) }] },
  { id: novoId(), defId: 'heater', vendedor: 'Darrow', seu: false, qty: 1, dur: 30, durMax: 40, material: null, lanceMinimo: 3100, compraJa: 3100, terminaEm: em(22), lances: [] }
];
const presos: { id: string; defId: string; qty: number; motivo: string }[] = [];

const ativos = () => lotes.filter(l => Date.parse(l.terminaEm) > Date.now());
const precoDe = (l: Lote) => {
  if (l.lances.length === 0) return l.lanceMinimo;
  const tetos = l.lances.map(b => b.teto).sort((a, b) => b - a);
  return tetos.length > 1 ? Math.min(tetos[0], Math.ceil(tetos[1] * 1.05)) : l.lanceMinimo;
};
const proximoDe = (l: Lote) => (l.lances.length ? Math.ceil(precoDe(l) * 1.05) : l.lanceMinimo);
const nomeDe = (defId: string) => MOCK_ITEM_DEFS.get(baseDe(defId))?.name ?? defId;

function linha(l: Lote): MercadoOfertaLinha {
  return {
    id: l.id, vendedor: l.vendedor, seu: l.seu, qty: l.qty, dur: l.dur, durMax: l.durMax,
    defId: l.material ? `${l.defId}@${l.material}` : l.defId, nome: nomeDe(l.defId), material: l.material,
    lances: l.lances.length, terminaEm: l.terminaEm, preco: precoDe(l), proximoLance: proximoDe(l),
    compraJa: l.compraJa, lanceMinimo: l.lanceMinimo
  };
}

export function mockMercado(saldo: () => { cofre: number; mochila: number }, pagar: (n: number) => void, quem: () => string) {
  const unitario = (l: Lote) => (MOCK_ITEM_DEFS.get(l.defId)?.stackable && l.qty > 1 ? Math.ceil(precoDe(l) / l.qty) : precoDe(l));
  return {
    catalogo(): MercadoCatalogo {
      const itens: MercadoCatalogoLinha[] = [...MOCK_ITEM_DEFS.values()].filter(d => !d.id.includes('@') && !d.id.includes(':')).map(d => {
        const ls = ativos().filter(l => l.defId === d.id);
        return {
          defId: d.id, nome: d.name, grupo: grupoMock(d.id, d.equipSlot), stackable: d.stackable, peso: d.weight, ar: 0, dano: null,
          ofertas: ls.length, menorPreco: ls.length ? Math.min(...ls.map(unitario)) : null,
          lojaPreco: LOJA[d.id] ?? null, lojaOficio: null
        };
      });
      return { itens, total: itens.length, comOferta: itens.filter(i => i.ofertas).length, saldo: saldo() };
    },
    item(defId: string, q: { material?: string; soImediata?: boolean; soPago?: boolean; ordem?: string } = {}): MercadoItem {
      const base = baseDe(defId);
      const def = MOCK_ITEM_DEFS.get(base);
      if (!def) throw new ApiError('Este item não existe.', 404);
      const livre = saldo().cofre + saldo().mochila;
      let ls = ativos().filter(l => l.defId === base);
      const materiais = [...new Set(ls.map(l => l.material).filter((m): m is string => !!m))];
      if (q.material) ls = ls.filter(l => l.material === q.material);
      if (q.soImediata) ls = ls.filter(l => l.compraJa != null);
      if (q.soPago) ls = ls.filter(l => (l.compraJa ?? proximoDe(l)) <= livre);
      ls.sort(q.ordem === 'termina' ? (a, b) => Date.parse(a.terminaEm) - Date.parse(b.terminaEm)
        : q.ordem === 'caro' ? (a, b) => unitario(b) - unitario(a) : (a, b) => unitario(a) - unitario(b));
      return {
        defId: base, nome: def.name, grupo: grupoMock(base, def.equipSlot), stackable: def.stackable, peso: def.weight, ar: 0,
        dano: def.equipSlot === 'weapon' ? '5–24' : null, lojaPreco: LOJA[base] ?? null, lojaOficio: null,
        mediana: ls.length ? Math.round(Math.min(...ls.map(unitario)) * 0.92) : null, vendidas: ls.length ? 12 : 0,
        familia: BASES_COM_MATERIAL[base] ?? null, materiais, ofertas: ls.map(linha), saldo: saldo()
      };
    },
    oferta(lotId: string): MercadoOferta {
      const l = lotes.find(x => x.id === lotId);
      if (!l) throw new ApiError('Este lote não existe.', 404);
      const def = MOCK_ITEM_DEFS.get(l.defId);
      return {
        id: l.id, defId: l.defId, nome: nomeDe(l.defId), grupo: grupoMock(l.defId, def?.equipSlot ?? 'none'), qty: l.qty, dur: l.dur, durMax: l.durMax,
        material: l.material, peso: def?.weight ?? 0, ar: 0, dano: null, vendedor: l.vendedor, seu: l.seu, podeCancelar: l.seu && l.lances.length === 0,
        terminaEm: l.terminaEm, lanceMinimo: l.lanceMinimo, preco: precoDe(l), proximoLance: proximoDe(l), compraJa: l.compraJa,
        lojaPreco: LOJA[l.defId] ?? null, lojaOficio: null, mediana: null,
        historico: [...l.lances].sort((a, b) => b.teto - a.teto).map(b => ({ em: b.em, nome: b.nome, teto: b.teto, seu: b.seu })),
        saldo: saldo()
      };
    },
    meus(): MercadoMeus {
      const meus = ativos().filter(l => l.seu);
      const meusLances = ativos().filter(l => l.lances.some(b => b.seu));
      return {
        anunciosAtivos: meus.length, anunciosTeto: 10,
        anuncios: meus.map(l => ({ id: l.id, defId: l.defId, nome: nomeDe(l.defId), qty: l.qty, material: l.material, preco: precoDe(l), lances: l.lances.length, terminaEm: l.terminaEm, estado: 'ativo', lanceMinimo: l.lanceMinimo, compraJa: l.compraJa })),
        lances: meusLances.map(l => {
          const meu = Math.max(...l.lances.filter(b => b.seu).map(b => b.teto));
          const maior = Math.max(...l.lances.map(b => b.teto));
          return { lotId: l.id, defId: l.defId, nome: nomeDe(l.defId), material: l.material, teto: meu, preco: precoDe(l), vencendo: meu >= maior, terminaEm: l.terminaEm };
        }),
        retirar: presos.map(p => ({ ...p, nome: nomeDe(p.defId) })),
        saldo: saldo(), cofreUsado: 12, cofreVagas: 50
      };
    },
    lance(lotId: string, teto: number): MercadoOferta {
      const l = lotes.find(x => x.id === lotId);
      if (!l) throw new ApiError('Este lote não existe.', 404);
      if (l.seu) throw new ApiError('Não dá para dar lance no próprio anúncio.', 409);
      if (teto < proximoDe(l)) throw new ApiError(`O lance mínimo agora é ${proximoDe(l)}.`, 409);
      if (teto > saldo().cofre + saldo().mochila) throw new ApiError('Seu saldo não cobre esse teto.', 409);
      l.lances = l.lances.filter(b => !b.seu);
      l.lances.push({ nome: quem(), teto, seu: true, em: new Date().toISOString() });
      return this.oferta(lotId);
    },
    comprarJa(lotId: string) {
      const i = lotes.findIndex(x => x.id === lotId);
      const l = lotes[i];
      if (!l || l.compraJa == null) throw new ApiError('Este lote não tem compra já.', 409);
      if (l.compraJa > saldo().cofre + saldo().mochila) throw new ApiError('Seu saldo não cobre.', 409);
      pagar(l.compraJa);
      lotes.splice(i, 1);
      return { id: lotId };
    },
    anunciar(defId: string, qty: number, dur: number | null, durMax: number | null, lanceMinimo: number, compraJa: number | null, horas: number) {
      if (lanceMinimo < 1) throw new ApiError('Diga o lance mínimo.', 400);
      const l: Lote = { id: novoId(), defId, vendedor: quem(), seu: true, qty, dur, durMax, material: null, lanceMinimo, compraJa, terminaEm: em(horas), lances: [] };
      lotes.push(l);
      return { id: l.id };
    },
    cancelar(lotId: string) {
      const i = lotes.findIndex(x => x.id === lotId && x.seu);
      if (i < 0) throw new ApiError('Este lote não é seu.', 409);
      if (lotes[i].lances.length) throw new ApiError('Há lance. Vai até o fim.', 409);
      const [l] = lotes.splice(i, 1);
      return l;
    },
    retirar(id: string) {
      const i = presos.findIndex(p => p.id === id);
      if (i >= 0) presos.splice(i, 1);
    }
  };
}
